import React, { useState } from 'react';
import {
    SpecTableChecked,
    RuleResultSpecSymbol,
    RuleResultSpecBarcode,
    SpecSymbolResults,
    SpecBarcodeResults,
    RuleResult,
    SpecRuleType,
    Outcome,
    SpecTableResults,
    SpecStringResults,
    SpecStringListResults,
} from '../types';
import {
    faExclamation,
    faCopy as faCopySolid,
    faEye as faEyeSolid,
} from '@fortawesome/free-solid-svg-icons';
import Tooltip from '@mui/material/Tooltip';
import {
    faCopy,
    faEye,
    faQuestionCircle,
} from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SpecCheckDetailsText from './SpecCheckDetailsText';
import SpecCheckDetailsTableNew from './SpecCheckDetailsTableNew';
import SpecCheckDetailsSymbol from './SpecCheckDetailsSymbol';
import SpecCheckDetailsBarcode from './SpecCheckDetailsBarcode';
import { useLabelContext } from '../contexts/LabelContext';

interface RuleCardProps {
    ruleResult: RuleResult;
}

const colourMap = {
    [Outcome.FAIL]: 'red',
    [Outcome.WARNING]: '#eed202',
    [Outcome.PASS]: '#5cb85c',
};

const RuleCard: React.FC<RuleCardProps> = ({ ruleResult }) => {
    const [active, setActive] = useState(false);
    // const [focussed, setFocussed] = useState(false);
    const [copied, setCopied] = useState(false);

    const {
        state: { focussedRule },
        setters: { setFocussedRule },
    } = useLabelContext();
    const focussed = focussedRule === ruleResult.name;

    const handleCopyClick = (copyText: string) => {
        navigator.clipboard
            .writeText(copyText)
            .then(() => {
                setCopied(true); // Change the icon to a checkmark
                setTimeout(() => setCopied(false), 5000); // Change the icon back to the copy icon after 10 seconds
            })
            .catch((err) => {
                console.error('Failed to copy text: ', err);
            });
    };
    function renderCheckDetails() {
        switch (ruleResult.type) {
            case SpecRuleType.SPEC_STRING:
                return <SpecCheckDetailsText ruleResult={ruleResult} />;
            case SpecRuleType.SPEC_STRING_LIST:
                return <SpecCheckDetailsText ruleResult={ruleResult} />;
            case SpecRuleType.SPEC_TABLE:
                return (
                    <SpecCheckDetailsTableNew
                        specTableResults={
                            ruleResult.output_data as SpecTableResults
                        }
                    />
                );
            case SpecRuleType.SPEC_SYMBOL:
                return (
                    <SpecCheckDetailsSymbol
                        ruleResult={ruleResult as RuleResultSpecSymbol}
                    />
                );
            case SpecRuleType.SPEC_BARCODE:
                return (
                    <SpecCheckDetailsBarcode
                        ruleResult={ruleResult as RuleResultSpecBarcode}
                    />
                );
            default:
                return null;
        }
    }
    const handleFocus = () => {
        if (canFocus()) {
            if (focussed) {
                setFocussedRule(null);
            } else {
                setFocussedRule(ruleResult.name);
            }
        }
    };
    function canFocus() {
        switch (ruleResult.type) {
            case SpecRuleType.SPEC_STRING:
                return (
                    ruleResult.output_data as SpecStringResults
                ).match_sequence.checked_tokens.some(
                    (token) => token.label_token !== null,
                );
            case SpecRuleType.SPEC_STRING_LIST:
                return (
                    ruleResult.output_data as SpecStringListResults
                ).match_sequences.some((sequence) =>
                    sequence.checked_tokens.some(
                        (token) => token.label_token !== null,
                    ),
                );
            case SpecRuleType.SPEC_TABLE:
                return (
                    (ruleResult.output_data as SpecTableResults).headers.some(
                        (header) =>
                            header.checked_tokens.some(
                                (token) => token.label_token !== null,
                            ),
                    ) ||
                    (ruleResult.output_data as SpecTableResults).rows.some(
                        (row) =>
                            row.some((cell) =>
                                cell.checked_tokens.some(
                                    (token) => token.label_token !== null,
                                ),
                            ),
                    )
                );
            case SpecRuleType.SPEC_SYMBOL:
                return (
                    (ruleResult.output_data as SpecSymbolResults)
                        .detected_symbols.length > 0
                );
            case SpecRuleType.SPEC_BARCODE:
                return (
                    (ruleResult.output_data as SpecBarcodeResults)
                        .detected_barcodes.length > 0
                );
            default:
                return true;
        }
    }
    return (
        <div
            className="rules-check-card"
            onMouseEnter={() => setActive(true)}
            onMouseLeave={() => setActive(false)}
        >
            <div
                className="decision-colour-box"
                style={{
                    backgroundColor: colourMap[ruleResult.outcome],
                }}
            />
            <div className="content-container">
                <div className="upper-container">
                    <div className="text-container">
                        <p
                            className="check-name"
                            style={{ marginRight: '5px' }}
                        >
                            {ruleResult.display_name}
                        </p>
                        {ruleResult.description && (
                            <Tooltip
                                title={
                                    <p
                                        style={{
                                            margin: '0px',
                                            fontSize: '1.4em',
                                        }}
                                    >
                                        {ruleResult.description}
                                    </p>
                                }
                                placement="top"
                                arrow
                            >
                                <FontAwesomeIcon icon={faQuestionCircle} />
                            </Tooltip>
                        )}
                        <p className="check-summary">
                            {ruleResult.summary.text}
                        </p>
                        {ruleResult.summary.bullets && (
                            <ul className="check-summary-bullets">
                                {ruleResult.summary.bullets.map((bullet, i) => (
                                    <li key={i}>{bullet}</li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <div className="button-container">
                        {(active || focussed) && (
                            <>
                                {canFocus() && (
                                    <Tooltip
                                        title={
                                            <p
                                                style={{
                                                    margin: '0px',
                                                    fontSize: '1.4em',
                                                }}
                                            >
                                                {focussed
                                                    ? 'Unfocus'
                                                    : 'Focus on label'}
                                            </p>
                                        }
                                        placement="top"
                                    >
                                        <button
                                            onClick={() => handleFocus()}
                                            className={
                                                focussed ? 'focussed' : ''
                                            }
                                        >
                                            <FontAwesomeIcon
                                                icon={
                                                    focussed
                                                        ? faEyeSolid
                                                        : faEye
                                                }
                                            />
                                        </button>
                                    </Tooltip>
                                )}

                                {/* <Tooltip
                                    title={
                                        <p
                                            style={{
                                                margin: '0px',
                                                fontSize: '1.4em',
                                            }}
                                        >
                                            Feedback
                                        </p>
                                    }
                                    placement="top"
                                >
                                    <button
                                        onClick={() => handleFeedbackClick()}
                                    >
                                        <FontAwesomeIcon icon={faExclamation} />
                                    </button>
                                </Tooltip> */}
                                {ruleResult.copy_summary !== null && (
                                    <Tooltip
                                        title={
                                            <p
                                                style={{
                                                    margin: '0px',
                                                    fontSize: '1.4em',
                                                }}
                                            >
                                                {copied
                                                    ? 'Copied'
                                                    : 'Copy summary'}
                                            </p>
                                        }
                                        placement="top"
                                    >
                                        <button
                                            onClick={() => {
                                                if (ruleResult.copy_summary) {
                                                    handleCopyClick(
                                                        ruleResult.copy_summary,
                                                    );
                                                }
                                            }}
                                        >
                                            <FontAwesomeIcon
                                                icon={
                                                    copied
                                                        ? faCopySolid
                                                        : faCopy
                                                }
                                            />
                                        </button>
                                    </Tooltip>
                                )}
                            </>
                        )}
                    </div>
                </div>
                <hr style={{ margin: '5px 0px' }} />
                <div className="details-container">{renderCheckDetails()}</div>
            </div>
        </div>
    );
};

export default RuleCard;
